const React = require('react');
const { default: Switch } = require('@mui/material/Switch');
const { default: Styled } = require('styled-components');
const ControlledTextField = require('./ControlledTextField');
const ControlledSelectField = require('./ControlledSelectField');
const FieldContext = require('./FieldContext');
const SelectWithSpecifyUtils = require('./selectWithSpecifyUtils');

const internals = {};

module.exports = function SelectWithSpecifyField(props) {

    const {
        field,
        fieldName = field.name,
        textFieldProps = {},
        useField: requestedUseFieldHook
    } = props;

    const {
        SelectFieldPositionAdjustment,
        SpecifyFieldPositionAdjustment
    } = internals;

    const resolvedUseField = typeof requestedUseFieldHook === 'function'
        ? requestedUseFieldHook
        : FieldContext.useField;

    const fieldController = resolvedUseField(fieldName);
    const value = fieldController.getValue() || SelectWithSpecifyUtils.buildOffValue({
        definition: field
    });
    const config = SelectWithSpecifyUtils.getConfig(field);
    const enabled = SelectWithSpecifyUtils.isEnabled({
        value,
        definition: field
    });
    const selectedOptionValue = SelectWithSpecifyUtils.getSelectedOptionValue({
        value,
        definition: field
    });
    const showSpecifyField = enabled && selectedOptionValue === config.specifyOptionValue;
    const baseTextFieldProps = fieldController.getTextFieldProps(textFieldProps);
    const selectTextFieldProps = showSpecifyField ? {
        ...baseTextFieldProps,
        error: false,
        helperText: null,
        inputRef: undefined
    } : baseTextFieldProps;
    const specifyTextFieldProps = {
        ...baseTextFieldProps,
        required: true
    };
    const fieldLabel = field.label || fieldController.definition.label || fieldName;
    const selectLabel = config.selectLabel || field.selectLabel || field.label;
    const specifyLabel = config.specifyLabel || field.specifyLabel || 'Specify';
    const options = internals.buildOptions({
        field,
        fieldController,
        config
    });

    return <internals.SelectWithSpecifyFieldRoot
        $showDetailFields={!config.enableSwitch || enabled}
    >
        {config.enableSwitch && (
            <internals.SwitchRow>
                <internals.SwitchLabel>
                    {fieldLabel}
                </internals.SwitchLabel>
                <internals.SwitchControl>
                    <internals.SwitchChoiceLabel>
                        {field.offLabel || 'No/Skip'}
                    </internals.SwitchChoiceLabel>
                    <Switch
                        checked={enabled}
                        color='primary'
                        inputProps={{
                            'aria-label': fieldLabel,
                            name: fieldName
                        }}
                        onChange={(event) => {

                            fieldController.setValue(event.target.checked
                                ? SelectWithSpecifyUtils.buildPendingValue({
                                    definition: field
                                })
                                : SelectWithSpecifyUtils.buildOffValue({
                                    definition: field
                                }), undefined, event);
                        }}
                    />
                    <internals.SwitchChoiceLabel>
                        {field.onLabel || 'Yes'}
                    </internals.SwitchChoiceLabel>
                </internals.SwitchControl>
            </internals.SwitchRow>
        )}
        {(!config.enableSwitch || enabled) && (
            <internals.DetailField>
                <SelectFieldPositionAdjustment>
                    <ControlledSelectField
                        label={selectLabel}
                        options={options}
                        value={selectedOptionValue}
                        onChange={(nextValue, event) => {

                            if (nextValue === config.specifyOptionValue) {
                                fieldController.setValue(SelectWithSpecifyUtils.buildSpecifyPlaceholderValue({
                                    definition: field
                                }), undefined, event);
                                return;
                            }

                            fieldController.setValue(nextValue !== config.defaultOptionValue
                                ? SelectWithSpecifyUtils.buildSelectedValue({
                                    definition: field,
                                    selectedValue: nextValue
                                })
                                : SelectWithSpecifyUtils.buildPendingValue({
                                    definition: field
                                }), undefined, event);
                        }}
                        textFieldProps={selectTextFieldProps}
                    />
                </SelectFieldPositionAdjustment>
            </internals.DetailField>
        )}
        {showSpecifyField && (
            <internals.DetailField>
                <SpecifyFieldPositionAdjustment>
                    <ControlledTextField
                        label={specifyLabel}
                        value={SelectWithSpecifyUtils.getSpecifyTextValue({
                            value,
                            definition: field
                        })}
                        onChange={(nextValue, event) => {

                            fieldController.setValue(SelectWithSpecifyUtils.buildSpecifyTextValue({
                                definition: field,
                                textValue: nextValue
                            }), undefined, event);
                        }}
                        fullWidth
                        textFieldProps={specifyTextFieldProps}
                    />
                </SpecifyFieldPositionAdjustment>
            </internals.DetailField>
        )}
    </internals.SelectWithSpecifyFieldRoot>;
};

internals.resolveSourceOptions = ({
    field = {},
    fieldController
}) => {

    if (Array.isArray(field.options)) {
        return field.options;
    }

    const formProps = fieldController.formProps || {};

    if (typeof field.optionsSource === 'string' && Array.isArray(formProps[field.optionsSource])) {
        return formProps[field.optionsSource];
    }

    return [];
};

internals.buildOptions = ({
    field,
    fieldController,
    config
}) => [
    ...(!config.hideDefaultOption ? [{
        value: config.defaultOptionValue,
        label: config.defaultOptionLabel
    }] : []),
    ...internals.resolveSourceOptions({
        field,
        fieldController
    }),
    {
        value: config.specifyOptionValue,
        label: config.specifyOptionLabel
    }
];

internals.SelectWithSpecifyFieldRoot = Styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: ${({ theme }) => theme.spacing(2)};
    padding-bottom: ${({ $showDetailFields, theme }) => $showDetailFields ? theme.spacing(2) : 0};
`;

internals.SwitchRow = Styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    min-height: 38px;
`;

internals.SwitchLabel = Styled.div`
    flex: 1 1 auto;
    min-width: 0;
    font-size: 16px;
    line-height: 20px;
    color: ${({ theme }) => theme.palette.text.primary};
`;

internals.SwitchControl = Styled.div`
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: ${({ theme }) => theme.spacing(.5)};
    white-space: nowrap;
`;

internals.SwitchChoiceLabel = Styled.span`
    font-size: 12px;
    line-height: 16px;
    color: ${({ theme }) => theme.palette.text.secondary};
`;

internals.SelectFieldPositionAdjustment = Styled.div`
    .MuiFormControl-root {
        margin-top: 0;
        margin-bottom: 0;
    }
`;

internals.SpecifyFieldPositionAdjustment = Styled.div`
    .MuiFormControl-root {
        margin-top: 0;
        margin-bottom: 0;
    }
`;

internals.DetailField = Styled.div`
    margin-top: 0;
`;

module.exports.internals = internals;
